import React, { useState } from "react";
import { Tabs, Modal, Form, Input, message } from "antd";
import { UserOutlined, TeamOutlined, BankOutlined } from "@ant-design/icons";
import styled from "styled-components";
import MyProfileTab from "./MyProfileTab";
import InviteUsersTab from "./InviteUsersTab";
import MyCompanyTab from "./MyCompanyTab";
import { API_CONST } from "../../const";

const AccountWrapper = styled.div`
  padding: 24px 0;

  .page-title {
    font-size: 28px;
    font-weight: 600;
    color: #014c75;
    margin-bottom: 24px;
  }

  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    padding: 12px 24px;
    font-size: 15px;
    
    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: #014c75;
        font-weight: 500;
      }
    }
  }

  .ant-tabs-ink-bar {
    background: #00d9a9;
    height: 3px;
  }
`;

const Account = ({ role = "admin" }) => {
  const [activeTab, setActiveTab] = useState("my-profile");
  const [companyRefreshKey, setCompanyRefreshKey] = useState(0);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm] = Form.useForm();

  const handleCompanyConnected = (newOrgName) => {
    localStorage.setItem("userOrganization", newOrgName);
    setCompanyRefreshKey((prev) => prev + 1);
    setActiveTab("my-company");
  };

  const handleChangePassword = () => {
    setChangePasswordVisible(true);
  };

  const handlePasswordSubmit = async () => {
    try {
      const values = await passwordForm.validateFields();
      
      if (values.newPassword !== values.confirmPassword) {
        message.error("New password and confirm password do not match");
        return;
      }

      setChangingPassword(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(API_CONST.CHANGE_MY_PASSWORD, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        }),
      });

      if (response.status === 401 || response.status === 403) {
        message.error("Session expired. Please login again.");
        localStorage.removeItem("authToken");
        window.location.href = "/";
        return;
      }

      const data = await response.json();
      if (response.ok) {
        message.success("Password changed successfully!");
        setChangePasswordVisible(false);
        passwordForm.resetFields();
      } else {
        message.error(data.message || "Failed to change password");
      }
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error("Failed to change password. Please try again.");
    } finally {
      setChangingPassword(false);
    }
  };

  const tabItems = [
    {
      key: "my-profile",
      label: (
        <span>
          <UserOutlined style={{ marginRight: 8 }} />
          My Profile
        </span>
      ),
      children: <MyProfileTab onChangePassword={handleChangePassword} onCompanyConnected={handleCompanyConnected} />,
    },
    {
      key: "my-company",
      label: (
        <span>
          <BankOutlined style={{ marginRight: 8 }} />
          My Company
        </span>
      ),
      children: <MyCompanyTab refreshKey={companyRefreshKey} />,
    },
    {
      key: "invite-users",
      label: (
        <span>
          <TeamOutlined style={{ marginRight: 8 }} />
          Invite User
        </span>
      ),
      children: <InviteUsersTab role={role} refreshKey={companyRefreshKey} />,
    },
  ];

  return (
    <AccountWrapper>
      <h1 className="page-title">Account</h1>
      
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
      />

      <Modal
        title="Change Password"
        open={changePasswordVisible}
        onOk={handlePasswordSubmit}
        onCancel={() => {
          setChangePasswordVisible(false);
          passwordForm.resetFields();
        }}
        okText="Change Password"
        okButtonProps={{ 
          loading: changingPassword,
          style: { background: "#00d9a9", borderColor: "#00d9a9" } 
        }}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[{ required: true, message: "Please enter current password" }]}
          >
            <Input.Password placeholder="Enter current password" />
          </Form.Item>
          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: "Please enter new password" },
              { min: 6, message: "Password must be at least 6 characters" },
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            rules={[
              { required: true, message: "Please confirm new password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </AccountWrapper>
  );
};

export default Account;
